import { FC, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-daisyui';
import { IconMenu2, IconX, IconUser } from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useAuthContext } from '@/context/AuthContext';
import Routes from '@/router/Routes';
import { useTracking } from '@/hooks/useTracking';

/**
 * Navigation link configuration
 * Supports both smooth scroll links (to section IDs) and routing links (to routes)
 */
interface SmoothScrollLink {
  label: string;
  type: 'scroll';
  sectionId: string;
}

interface RouteLink {
  label: string;
  type: 'route';
  route: string;
}

type NavLink = SmoothScrollLink | RouteLink;

const NAV_LINKS: NavLink[] = [
  { label: 'List on Gympera', type: 'route', route: Routes.REGISTER_OWNER },
];

/**
 * Smooth scroll utility
 * Scrolls to target section with smooth animation
 */
const smoothScrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

/**
 * NavLinks Component
 * Route-aware: on landing page smooth scrolls, on other pages navigates to landing page with hash
 */
interface NavLinksProps {
  className?: string;
  onClick?: () => void;
  isLandingPage: boolean;
}

const NavLinks: FC<NavLinksProps> = ({ className = '', onClick, isLandingPage }) => {
  const navigate = useNavigate();

  return (
    <>
      {NAV_LINKS.map((link) => {
        if (link.type === 'route') {
          return (
            <Link
              key={link.route}
              to={link.route}
              onClick={onClick}
              className={`text-white hover:text-white/80 transition-colors duration-200 rounded px-2 py-1 ${className}`}
              aria-label={`Navigate to ${link.label}`}
            >
              {link.label}
            </Link>
          );
        }

        return (
          <button
            key={link.sectionId}
            onClick={() => {
              if (isLandingPage) {
                smoothScrollToSection(link.sectionId);
              } else {
                navigate(`/${link.sectionId ? `#${link.sectionId}` : ''}`);
              }
              onClick?.();
            }}
            className={`text-white hover:text-white/80 transition-colors duration-200 rounded px-2 py-1 ${className}`}
            aria-label={`Navigate to ${link.label} section`}
          >
            {link.label}
          </button>
        );
      })}
    </>
  );
};

/**
 * MobileMenu Component
 * Mobile slide-in menu with navigation links and auth-aware actions
 */
function getDashboardRoute(role?: string): string {
  switch (role) {
    case 'admin':
      return Routes.ADMIN_GYM_OWNERS;
    case 'owner':
      return Routes.OWNER_LISTING_STATUS;
    default:
      return Routes.GOER_SAVED_GYMS;
  }
}

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isLandingPage: boolean;
  isAuthenticated: boolean;
  onLogout: () => void;
  dashboardRoute: string;
}

const MobileMenu: FC<MobileMenuProps> = ({ isOpen, onClose, isAuthenticated, onLogout, dashboardRoute }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Menu Panel */}
          <motion.div
            className="fixed top-0 right-0 h-full w-64 bg-base-100 shadow-xl z-50 flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation menu"
          >
            {/* Close Button */}
            <div className="flex justify-end p-4 border-b border-base-300">
              <button
                onClick={onClose}
                className="text-base-content hover:text-primary transition-colors rounded p-2"
                aria-label="Close mobile menu"
              >
                <IconX className="w-6 h-6" />
              </button>
            </div>

            {/* Auth Action — top of menu */}
            <div className="p-6 border-b border-base-300">
              {isAuthenticated ? (
                <div className="flex flex-col gap-3">
                  <Link to={dashboardRoute} onClick={onClose}>
                    <Button
                      className="border-0 text-white font-semibold w-full"
                      style={{
                        background:
                          'linear-gradient(160deg, #2d5a2d 0%, #336633 40%, #3f733f 60%, #336633 80%, #2d5a2d 100%)',
                      }}
                    >
                      Dashboard
                    </Button>
                  </Link>
                  <Button
                    className="btn btn-outline btn-primary w-full font-semibold"
                    onClick={() => {
                      onLogout();
                      onClose();
                    }}
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <Link to="/login" onClick={onClose}>
                  <Button
                    className="border-0 text-white font-semibold w-full"
                    style={{
                      background:
                        'linear-gradient(160deg, #2d5a2d 0%, #336633 40%, #3f733f 60%, #336633 80%, #2d5a2d 100%)',
                    }}
                  >
                    Log In
                  </Button>
                </Link>
              )}
            </div>

            {/* Navigation Links */}
            <nav className="flex flex-col gap-4 p-6 flex-1">
              <Link to={Routes.REGISTER_OWNER} onClick={onClose} className="w-full">
                <Button className="btn btn-outline btn-primary w-full font-semibold">
                  List on Gympera
                </Button>
              </Link>
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

interface HeaderProps extends PropsWithClassName {
  /**
   * When provided, the mobile menu button calls this instead of opening the
   * site-wide mobile menu — used by the owner/admin dashboard layout so the
   * same header's hamburger button opens the dashboard nav drawer instead.
   */
  onMobileMenuToggle?: () => void;
}

/**
 * Header Component
 * Site-wide header with metallic green gradient, route-aware navigation, and auth-aware actions
 */
const Header: FC<HeaderProps> = ({ className = '', onMobileMenuToggle }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { session, user, logout } = useAuthContext();
  const { track } = useTracking();

  const isLandingPage = location.pathname === '/';
  const isAuthenticated = !!session;

  const handleLogoClick = () => {
    if (isLandingPage) {
      smoothScrollToSection('hero-section');
    } else {
      navigate('/');
    }
  };

  const toggleMobileMenu = () => {
    if (onMobileMenuToggle) {
      onMobileMenuToggle();
      return;
    }
    setIsMobileMenuOpen((prev) => {
      if (!prev && isLandingPage) {
        track('landing.mobile_menu_open', {});
      }
      return !prev;
    });
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isMobileMenuOpen) {
      closeMobileMenu();
    }
  };

  return (
    <header
      className={`w-full ${className}`}
      style={{
        background:
          'linear-gradient(160deg, #2d5a2d 0%, #336633 40%, #3f733f 60%, #336633 80%, #2d5a2d 100%)',
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="max-w-7xl xl:max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={handleLogoClick}
            className="flex-shrink-0 rounded"
            aria-label="Gympera - Go to home"
          >
            <img
              src="/images/logo-03.png"
              alt="Gympera Logo"
              className="h-7 md:h-8 w-auto"
              loading="eager"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8" aria-label="Main navigation">
            <NavLinks isLandingPage={isLandingPage} />
            {isAuthenticated ? (
              <Button
                onClick={() => navigate(getDashboardRoute(user?.role))}
                className="btn border-none rounded-full px-3 hover:bg-white/10 transition-colors bg-transparent"
                size="md"
              >
                <IconUser color="white" className="w-6 h-6" />
              </Button>
            ) : (
              <Link
                to="/login"
                className="text-white hover:text-white/80 transition-colors duration-200 rounded px-2 py-1"
              >
                Log In
              </Link>
            )}
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            onClick={toggleMobileMenu}
            className={`${onMobileMenuToggle ? 'lg:hidden' : 'md:hidden'} text-white hover:text-white/80 transition-colors rounded p-2`}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <IconX className="w-6 h-6" />
            ) : (
              <IconMenu2 className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {!onMobileMenuToggle && (
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={closeMobileMenu}
          isLandingPage={isLandingPage}
          isAuthenticated={isAuthenticated}
          onLogout={logout}
          dashboardRoute={getDashboardRoute(user?.role)}
        />
      )}
    </header>
  );
};

export default Header;
